// PasswordChangeModal.js
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Visibility, VisibilityOff, CheckCircle, Cancel } from "@mui/icons-material";
import { useNotification } from "../context/NotificationContext";
import { api } from "../services/api";

const PasswordChangeModal = ({ open, onClose, email, isFirstLogin = false, onSignOut }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const { showNotification } = useNotification();

  // Password validation criteria
  const validatePassword = (password) => {
    const criteria = [
      { id: "length", text: "At least 8 characters", test: password.length >= 8 },
      { id: "uppercase", text: "One uppercase letter", test: /[A-Z]/.test(password) },
      { id: "lowercase", text: "One lowercase letter", test: /[a-z]/.test(password) },
      { id: "number", text: "One number", test: /\d/.test(password) },
      {
        id: "special",
        text: "One special character",
        test: /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(password),
      },
    ];
    return criteria;
  };

  const passwordCriteria = validatePassword(newPassword);
  const isPasswordValid = passwordCriteria.every((criterion) => criterion.test);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setValidationErrors([]);

    // Client-side validation
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    // Check if new password is same as current password
    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      return;
    }

    if (!isPasswordValid) {
      setError("Password does not meet the required criteria");
      setValidationErrors(passwordCriteria.filter((criterion) => !criterion.test));
      return;
    }

    setLoading(true);

    try {
      await api.changePassword({
        email,
        current_password: currentPassword,
        new_password: newPassword,
      });

      showNotification("Password changed successfully", "success");

      // If it's a first login, we need to mark it as completed and navigate
      if (isFirstLogin) {
        // Clear the first login flag from stored user data
        const userStorage = localStorage.getItem("user") || sessionStorage.getItem("user");
        if (userStorage) {
          const userData = JSON.parse(userStorage);
          userData.isfirstlogin = false;

          // Update the appropriate storage
          if (localStorage.getItem("user")) {
            localStorage.setItem("user", JSON.stringify(userData));
          } else {
            sessionStorage.setItem("user", JSON.stringify(userData));
          }
        }
      }

      onClose();
    } catch (err) {
      console.error("Password change error:", err);

      // Parse error response properly
      let errorMessage = "Failed to change password";
      let validationErrors = [];

      if (err.response && err.response.data) {
        if (err.response.data.detail) {
          if (typeof err.response.data.detail === "string") {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            // Handle validation errors array
            validationErrors = err.response.data.detail.map((error) => ({
              field: error.loc ? error.loc.join(".") : "password",
              message: error.msg || error.message || "Validation error",
            }));
            errorMessage = "Password validation failed";
          }
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message && !err.message.includes("[object Object]")) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (_, reason) => {
    // Prevent closing the dialog if it's a first login and user tries to escape or click outside
    if (isFirstLogin && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    if (!isFirstLogin) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isFirstLogin}
    >
      <DialogTitle>
        {isFirstLogin ? "Set Your New Password" : "Change Password"}
        {isFirstLogin && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You must change your password before continuing.
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            {validationErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Validation errors:
                </Typography>
                <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                  {validationErrors.map((validationError, index) => (
                    <li key={index}>
                      <Typography variant="body2">
                        {validationError.message || validationError.text}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="currentPassword"
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            id="currentPassword"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password Criteria Checklist */}
          {newPassword && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Password Requirements:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {passwordCriteria.map((criterion) => (
                  <ListItem key={criterion.id} sx={{ py: 0, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {criterion.test ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Cancel color="error" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={criterion.text}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: criterion.test ? "success.main" : "error.main",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            helperText={
              confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : ""
            }
            error={confirmPassword && newPassword !== confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {!isFirstLogin && <Button onClick={onClose}>Cancel</Button>}
        {isFirstLogin && onSignOut && <Button onClick={onSignOut}>Sign out</Button>}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isPasswordValid || newPassword !== confirmPassword}
        >
          {loading ? "Changing Password..." : isFirstLogin ? "Set Password" : "Change Password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordChangeModal;
